/**
 * admin_database_environments_controller.js — Admin > Database > Environments (Stimulus).
 *
 * Copies the comparison's SQL, guards the (not yet built) apply button, and
 * runs the "compare other schemas" form, drawing its result in place. Ported
 * from an inline <script> that rendered twice (its block sat inside
 * {% block content %}): the head copy threw before the body existed and the
 * document click listener piled up on every visit.
 *
 * Targets: sql (the rendered comparison SQL), schemaName (the compare form's
 * input), result (where a comparison is drawn). Copy buttons inside a drawn
 * result carry data-copy-target and are handled by #copyFromResult on the
 * result container, so nothing listens on document.
 *
 * Template: templates/admin/database/environments.html
 */

import { Controller } from "@hotwired/stimulus";

function copied(text) {
    navigator.clipboard.writeText(text).then(function () {
        alert('SQL copied to clipboard!');
    });
}

export default class extends Controller {
    static targets = ['sql', 'schemaName', 'result'];

    copyDiff() {
        copied(this.sqlTarget.textContent);
    }

    confirmApply() {
        if (confirm('WARNING: This will modify the production database. Are you sure?')) {
            alert('Apply functionality coming soon. Please run the SQL manually for now.');
        }
    }

    copyFromResult(event) {
        const button = event.target.closest('[data-copy-target]');
        const target = button && document.getElementById(button.dataset.copyTarget);
        if (target) copied(target.textContent);
    }

    compare(event) {
        event.preventDefault();
        const schemaName = this.schemaNameTarget.value.trim();
        if (!schemaName) return;

        const resultDiv = this.resultTarget;
        resultDiv.style.display = 'block';
        resultDiv.innerHTML = '<div class="text-center"><div class="spinner-border"></div><p class="mt-2">Comparing schemas...</p></div>';

        fetch('/admin/database/api/compare-environments', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ schema: schemaName })
        })
        .then(function (response) { return response.json(); })
        .then(function (data) { resultDiv.innerHTML = comparisonHtml(schemaName, data); })
        .catch(function (error) { resultDiv.innerHTML = errorHtml(error.message); });
    }
}

function comparisonHtml(schemaName, data) {
    if (data.identical) {
        return '<div class="alert alert-success"><i class="bi bi-check-circle me-2"></i>'
            + '<strong>In Sync!</strong> <code>' + schemaName + '</code> matches between dev and prod.</div>';
    }
    if (!data.diff_sql) return errorHtml(data.error || 'Failed to compare schemas');
    return '<div class="alert alert-danger"><i class="bi bi-exclamation-triangle me-2"></i>'
        + '<strong>Out of Sync: <code>' + schemaName + '</code></strong></div>'
        + differenceSummary(data.differences)
        + '<h6>SQL to run on production:</h6>'
        + '<pre class="p-3 bg-dark text-light rounded" style="max-height: 300px; overflow-y: auto;"><code id="compareDiffSql">' + data.diff_sql + '</code></pre>'
        + '<button class="btn btn-sm btn-outline-secondary" data-copy-target="compareDiffSql">'
        + '<i class="bi bi-clipboard me-1"></i>Copy</button>';
}

function differenceSummary(differences) {
    if (!differences) return '';
    let summary = '';
    if (differences.columns_to_add && differences.columns_to_add.length > 0) {
        summary += '<p class="text-danger mb-1"><strong>' + differences.columns_to_add.length + ' missing columns</strong></p>';
    }
    if (differences.tables_to_create && differences.tables_to_create.length > 0) {
        summary += '<p class="text-warning mb-1"><strong>' + differences.tables_to_create.length + ' missing tables</strong></p>';
    }
    return summary;
}

function errorHtml(message) {
    return '<div class="alert alert-danger"><i class="bi bi-x-circle me-2"></i><strong>Error:</strong> ' + message + '</div>';
}
